import React, { useCallback, useMemo, useState } from "react";

import { ChatBotMessage } from "@fartherfinance/frontend/api/Chatbot/Types";

import { CHATBOT_EVENTS } from "../../constants";
import { markdownListSplit } from "../../markdown";
import { ChatAnalytics } from "../../utils/analyticsContext";
import { trackChatbotEvent } from "../../utils/trackChatbotEvent";

import CopyButton from "./CopyButton";
import MessageSection from "./MessageSection";

import localStyles from "./ChatMessage.module.css";
import globalStyles from "../../style/ChatBotWindow.module.css";

interface ChatMessageProps {
  message: ChatBotMessage;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  const isAssistant = useMemo(
    () => message.role === "assistant",
    [message.role]
  );
  const isMultiLine = useMemo(
    () => message.content.includes("\n"),
    [message.content]
  );

  const bubbleClassName = useMemo(() => {
    const roleClass = isAssistant
      ? globalStyles.chatLeft
      : globalStyles.chatRight;

    const multiLineClass = isMultiLine ? globalStyles.multiLine : "";

    return `${roleClass} ${multiLineClass} ${localStyles.messageWrapper}`.trim();
  }, [isAssistant, isMultiLine]);

  const contentSections = useMemo(() => {
    return markdownListSplit(message.content).split("\n\n");
  }, [message.content]);

  const handleCopy = useCallback((content: string, messageId: string) => {
    navigator.clipboard.writeText(content);

    setCopiedMessageId(messageId);

    const sessionId = ChatAnalytics.getSessionId();

    trackChatbotEvent(CHATBOT_EVENTS.COPY_MESSAGE, {
      messageId,
      messageLength: content.length,
      ...(sessionId && { sessionId }),
    });

    setTimeout(() => {
      setCopiedMessageId(null);
    }, 2000);
  }, []);

  const isCopied = useMemo(
    () => copiedMessageId === message.messageId,
    [copiedMessageId, message.messageId]
  );

  return (
    <div className={bubbleClassName}>
      {contentSections.map((section, index) => (
        <MessageSection
          key={`${message.messageId}-${index}`}
          content={section}
          index={index}
          messageId={message.messageId}
          role={message.role}
        />
      ))}

      {isAssistant && (
        <CopyButton
          content={message.content}
          messageId={message.messageId}
          isCopied={isCopied}
          onCopy={handleCopy}
        />
      )}
    </div>
  );
};

export default React.memo(ChatMessage);
